<template>
  <div class="staff-list">
    <b-card no-body class="card-custom" header-tag="header" footer-tag="footer">
      <template v-slot:header>
        <div class="card-title">
          <h3 class="card-label">
            {{ $t("MENU.ITEM.SETTINGS.STAFF") }}
            <i class="mr-2"></i>
            <small class="">{{ $t("LABELS.LIST") }}</small>
          </h3>
        </div>
        <div class="card-toolbar">
          <router-link
            v-permission="['staff-create']"
            to="add"
            class="btn btn-light-success font-weight-bolder"
            ><i class="flaticon2-plus"></i>
            {{ $t("LABELS.ADD_NEW") }}</router-link
          >
        </div>
      </template>
      <v-card v-show="!isPageLoading">
        <ResponsiveFilterSheet
          :filters.sync="filters"
          :search-query.sync="searchQuery"
          @search="doSearch"
          @clear="doClear"
        >
          <template v-slot:desktop>
            <SearchTools
              :searchQuery.sync="searchQuery"
              @do-search="doSearch"
              @do-clear="doClear"
            >
              <template v-slot:prepend>
                <b-col class="pb-0" :md="4">
                  <b-form-group id="staff-active-group" label-for="staff-active-input">
                    <FormSelect
                      id="staff-active-input"
                      :placeholder="$t('MENU.ITEM.STATUS')"
                      type="select"
                      name="active"
                      :options="activeFilterOptions"
                      clearable
                      :value="filters.active"
                      @input="updateActiveFilter"
                    ></FormSelect>
                  </b-form-group>
                </b-col>
                <b-col class="pb-0 d-none d-md-block" :md="8"></b-col>
              </template>
            </SearchTools>
          </template>
          <template v-slot:mobile-fields="{ filters: draftFilters, updateFilter }">
            <FormSelect
              id="staff-active-input-mobile"
              :placeholder="$t('MENU.ITEM.STATUS')"
              :options="activeFilterOptions"
              clearable
              :value="draftFilters.active"
              @input="updateFilter('active', $event)"
            ></FormSelect>
          </template>
        </ResponsiveFilterSheet>
        <ResponsiveDataTable
          :headers="tableHeaders"
          :items="items"
          :options.sync="options"
          :items-per-page="totalItems"
          :server-items-length="totalItems"
          :loading="isLoadingStaff"
          loading-text="Loading... Please wait"
          :footer-props="{
            showFirstLastPage: true,
            itemsPerPageOptions: rowsPerPageItems,
            firstIcon: 'mdi-arrow-collapse-left',
            lastIcon: 'mdi-arrow-collapse-right'
          }"
          @update:options="getDataFromApi"
          class="px-6 elevation-1"
          :mobile-summary-fields="mobileSummaryFields"
          :mobile-detail-fields="mobileDetailFields"
          :mobile-toggle-label="$t('LABELS.DETAILS')"
        >

          <template v-slot:[`item.firstName`]="{ item }">
            {{ item.firstName }}
          </template>

          <template v-slot:[`item.lastName`]="{ item }">
              {{ item.lastName }}
          </template>

          <template v-slot:[`item.active`]="{ item }">
            <div
              v-if="checkIfUserHasPermission(['staff-update'])"
              class="d-flex align-center staff-active-control"
            >
              <v-checkbox
                class="ma-0 pa-0"
                :input-value="Number(item.active) === 1"
                :disabled="updatingActiveIds.includes(item.id)"
                :loading="updatingActiveIds.includes(item.id)"
                dense
                hide-details
                @change="updateActive(item, $event)"
              />
              <span class="ml-1">{{ textIsActive(item.active) }}</span>
            </div>
            <v-chip v-else :color="getColor(item.active)" small dark pill>
              <v-avatar left>
                <v-icon>{{ iconIsActive(item.active) }}</v-icon>
              </v-avatar>
              {{ textIsActive(item.active) }}
            </v-chip>
          </template>
          <template v-slot:[`item.staffTypeId`]="{ item }">
            <FormSelect
              v-if="checkIfUserHasPermission(['staff-update'])"
              :options="staffTypes"
              :value="item.staffTypeId"
              :disabled="updatingStaffTypeIds.includes(item.id)"
              clearable
              appendToBody
              @input="updateStaffType(item, $event)"
            />
            <span v-else>{{ staffTypeName(item.staffTypeId) }}</span>
          </template>
          <template v-slot:[`item.driverPassword`]="{ item }">
            <v-text-field
              v-if="isActiveDriver(item) && checkIfUserHasPermission(['staff-update'])"
              v-model="driverPasswords[item.id]"
              :placeholder="driverPasswordMask(item)"
              :disabled="updatingDriverPasswordIds.includes(item.id)"
              :loading="updatingDriverPasswordIds.includes(item.id)"
              type="password"
              dense
              hide-details
              @change="updateDriverPassword(item, $event)"
            />
            <span v-else>-</span>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <router-link
              v-if="checkIfUserHasPermission(['staff-update'])"
              :to="{ name: 'edit-staff', params: { id: item.id } }"
            >
              <v-tooltip right>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    class="mr-2"
                    fab
                    dark
                    x-small
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon dark>mdi-pencil</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("LABELS.EDIT") }}</span>
              </v-tooltip>
            </router-link>
            <template v-else>Няма права</template>
          </template>
          <template v-slot:mobile-summary-active="{ item }">
            <span class="staff-list__status d-inline-flex align-items-center">
              <span
                class="staff-list__status-dot rounded-circle mr-2"
                :class="{ 'staff-list__status-dot--inactive': !Number(item.active) }"
              ></span>
              {{ textIsActive(item.active) }}
            </span>
          </template>
          <template v-slot:mobile-summary-actions="{ item }">
            <v-btn
              v-if="checkIfUserHasPermission(['staff-update'])"
              :to="{ name: 'edit-staff', params: { id: item.id } }"
              :aria-label="$t('LABELS.EDIT')"
              dark
              fab
              x-small
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
          </template>
          <template v-slot:mobile-detail-active="{ item }">
            <div class="staff-list__mobile-control">
              <div v-if="checkIfUserHasPermission(['staff-update'])" class="staff-active-control d-flex align-center">
                <v-checkbox
                  class="ma-0 pa-0"
                  :input-value="Number(item.active) === 1"
                  :disabled="updatingActiveIds.includes(item.id)"
                  :loading="updatingActiveIds.includes(item.id)"
                  dense
                  hide-details
                  @change="updateActive(item, $event)"
                />
                <span class="ml-1">{{ textIsActive(item.active) }}</span>
              </div>
              <span v-else>{{ textIsActive(item.active) }}</span>
            </div>
          </template>
          <template v-slot:mobile-detail-staffType="{ item }">
            <FormSelect
              v-if="checkIfUserHasPermission(['staff-update'])"
              :options="staffTypes"
              :value="item.staffTypeId"
              :disabled="updatingStaffTypeIds.includes(item.id)"
              clearable
              appendToBody
              @input="updateStaffType(item, $event)"
            />
            <span v-else>{{ staffTypeName(item.staffTypeId) }}</span>
          </template>
          <template v-slot:mobile-detail-driverPassword="{ item }">
            <v-text-field
              v-if="isActiveDriver(item) && checkIfUserHasPermission(['staff-update'])"
              v-model="driverPasswords[item.id]"
              :placeholder="driverPasswordMask(item)"
              :disabled="updatingDriverPasswordIds.includes(item.id)"
              :loading="updatingDriverPasswordIds.includes(item.id)"
              type="password"
              dense
              hide-details
              @change="updateDriverPassword(item, $event)"
            />
            <span v-else>-</span>
          </template>
          <template v-slot:no-results>
            <v-alert :value="true" color="error" icon="warning">
              Your search for "{{ searchQuery }}" found no results.
            </v-alert>
          </template>
        </ResponsiveDataTable>
      </v-card>

      <v-skeleton-loader
        v-if="isPageLoading"
        :loading="isPageLoading"
        type="table"
      ></v-skeleton-loader>
    </b-card>
  </div>
</template>

<script>
//General
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import ApiService from "@/core/services/api.service";

import { mapGetters } from "vuex";

import SearchTools from "@/core/components/table/SearchTools.vue";
import ResponsiveDataTable from "@/core/components/table/ResponsiveDataTable.vue";
import ResponsiveFilterSheet from "@/core/components/table/ResponsiveFilterSheet.vue";
import FormSelect from "@/view/content/forms/components/FormSelect.vue";


import { FETCH_ALL_STAFF } from "@/modules/staff/store/staff.module";

import { permissionMixin } from "@/core/mixins/permissionMixin.js";

export default {
  mixins: [permissionMixin],
  data() {
    return {
      isPageLoading: true,
      tableLoading: true,
      items: [],
      staffTypes: [],
      updatingStaffTypeIds: [],
      updatingActiveIds: [],
      updatingDriverPasswordIds: [],
      driverPasswords: {},
      totalItems: 100,
      searchQuery: "",
      options: {
        descending: true,
        page: 1,
        itemsPerPage: 30,
        totalItems: 100
      },
      filters: {
        active: null
      },
      rowsPerPageItems: [10, 20, 30, 40, 50],
      columns: {},
      cancelSource: null,
      headers: [
        {
          text: "Id",
          align: "left",
          value: "id"
        },
        { text: this.$i18n.t('MENU.ITEM.STAFF.FIRST_NAME'), value: "firstName" },
        { text: this.$i18n.t('MENU.ITEM.STAFF.LAST_NAME'), value: "lastName" },
        { text: this.$i18n.t('FORM_LABELS.PHONE'), value: "phone" },
        { text: "Тип служител", value: "staffTypeId", width: "18%" },
        { text: this.$i18n.t('MENU.ITEM.STATUS'), value: "active" },
        { text: this.$i18n.t('DRIVER_ACCESS.PASSWORD'), value: "driverPassword", sortable: false },
        {
          align: "right",
          text: this.$i18n.t('MENU.ITEM.ACTIONS'),
          value: "actions",
          sortable: false,
          width: "10%"
        }
      ]
    };
  },
  components: {
    FormSelect,
    SearchTools,
    ResponsiveDataTable,
    ResponsiveFilterSheet
  },
  mounted() {
    let vm = this;
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: this.$i18n.t('MENU.ITEM.ASSETS.STAFF'), route: { name: "list-staff" } },
      { title: this.$i18n.t('MENU.ITEM.ASSETS.LIST') }
    ]);
    ApiService.get("staff-types").then(({ data }) => {
      vm.staffTypes = data.data;
    });
  },
  computed: {
    ...mapGetters(["getStaff", "isLoadingStaff"]),
    params() {
      return {
        ...this.options,
        ...this.filters,
        query: this.searchQuery
      };
    },
    activeFilterOptions() {
      return [
        { id: 1, name: this.textIsActive(1) },
        { id: 0, name: this.textIsActive(0) }
      ];
    },
    tableHeaders() {
      let vm = this;
      return vm.headers;
    },
    mobileSummaryFields() {
      return [
        {
          key: "name",
          label: this.$t("MENU.ITEM.SETTINGS.STAFF"),
          primary: true,
          value: item => this.staffName(item)
        },
        {
          key: "staffTypeId",
          label: this.$t("MENU.ITEM.STAFF.STAFF_TYPE"),
          value: item => this.staffTypeName(item.staffTypeId)
        },
        {
          key: "active",
          label: this.$t("MENU.ITEM.STATUS"),
          slot: "active"
        }
      ];
    },
    mobileDetailFields() {
      return [
        {
          key: "phone",
          label: this.$t("FORM_LABELS.PHONE")
        },
        {
          key: "hourlyRate",
          label: this.$t("MENU.ITEM.STAFF.HOURLY_RATE")
        },
        {
          key: "active",
          label: this.$t("MENU.ITEM.STATUS"),
          slot: "active",
          allowInDetails: true
        },
        {
          key: "staffTypeId",
          label: this.$t("MENU.ITEM.STAFF.STAFF_TYPE"),
          slot: "staffType",
          allowInDetails: true
        },
        {
          key: "driverPassword",
          label: this.$t("DRIVER_ACCESS.PASSWORD"),
          slot: "driverPassword"
        }
      ];
    }
  },
  methods: {
    cancelRequest() {
      //Axios cancelSource to stop current search if new value is entered
      if (this.cancelSource) {
        this.cancelSource.cancel("Start new search, stop active search");
      }
    },
    doSearch() {
      let vm = this;
      if (vm.options.page == 1) {
        vm.getDataFromApi();
      } else {
        vm.options.page = 1;
      }
    },
    doClear() {
      let vm = this;
      vm.searchQuery = "";
      vm.filters.active = null;
      vm.doSearch();
    },
    updateActiveFilter(active) {
      this.filters = {
        ...this.filters,
        active: active === undefined ? null : active
      };
      this.doSearch();
    },
    getDataFromApi() {
      let vm = this;
      // vm.tableLoading = true;
      //copy current params to modify
      let params = this.params;

      params.length = params.itemsPerPage; //set how many records to fecth per page
      params.start =
        params.page == 1 ? 0 : params.itemsPerPage * (params.page - 1); //set offset
      let apiParams = vm.$url.transformParams(params);
      vm.$store
        .dispatch(FETCH_ALL_STAFF, apiParams)
        .then(data => {
          vm.$nextTick(function() {
            vm.items = data.data.items;
            vm.driverPasswords = {};
            vm.items.forEach(item => vm.$set(vm.driverPasswords, item.id, ""));
            vm.totalItems = data.data.totalItemsCount;

            if (vm.isPageLoading == true) {
              vm.isPageLoading = false;
            }
          });
        })
        .catch(response => {
          console.log(response);
          if (response.status === 404) {
            vm.$notify({
              group: "notify",
              type: "error",
              text: "Nout Found"
            });
            this.$router.push({ name: "list-staff" });
          }
        });
    },
    getColor(active) {
      if (active == false || active == 0) return "red";
      else return "primary";
    },
    textIsActive(active) {
      if (active == false || active == 0) return "Неактивен";
      else return "Активен";
    },
    iconIsActive(active) {
      if (active == false || active == 0) return "mdi-close-circle-outline";
      else return "mdi-checkbox-marked-circle-outline";
    },
    staffTypeName(staffTypeId) {
      const staffType = this.staffTypes.find(type => type.id == staffTypeId);

      return staffType ? staffType.name : "-";
    },
    staffName(staff) {
      return `${staff.firstName || ""} ${staff.lastName || ""}`.trim() || "-";
    },
    isActiveDriver(staff) {
      const staffType = this.staffTypes.find(type => type.id == staff.staffTypeId);

      return Boolean(staff.active) && staffType?.slug === "driver";
    },
    driverPasswordMask(staff) {
      return staff.driverPasswordLength
        ? "*".repeat(staff.driverPasswordLength)
        : this.$t("AUTH.INPUT.PASSWORD");
    },
    updateActive(staff, active) {
      if (this.updatingActiveIds.includes(staff.id)) {
        return;
      }

      this.updatingActiveIds.push(staff.id);
      ApiService.put(`staff/${staff.id}/active`, { active })
        .then(({ data }) => {
          staff.active = data.data.active;
          staff.driverPasswordLength = data.data.driverPasswordLength;
          this.$set(this.driverPasswords, staff.id, "");
          this.$notify({
            group: "notify",
            type: "success",
            title: this.$t("ALERTS.SUCCESS"),
            text: "Статусът е обновен."
          });
        })
        .catch(() => {
          this.$notify({
            group: "notify",
            type: "error",
            title: this.$t("ALERTS.ERROR"),
            text: "Статусът не можа да бъде обновен."
          });
        })
        .finally(() => {
          this.updatingActiveIds = this.updatingActiveIds.filter(
            id => id !== staff.id
          );
        });
    },
    updateDriverPassword(staff, password) {
      if (!password || this.updatingDriverPasswordIds.includes(staff.id)) {
        return;
      }

      this.updatingDriverPasswordIds.push(staff.id);
      ApiService.put(`staff/${staff.id}/driver-password`, { driverPassword: password })
        .then(({ data }) => {
          staff.driverPasswordLength = data.data.driverPasswordLength;
          this.$set(this.driverPasswords, staff.id, "");
          this.$notify({
            group: "notify",
            type: "success",
            title: this.$t("ALERTS.SUCCESS"),
            text: "Паролата е обновена."
          });
        })
        .catch(() => {
          this.$notify({
            group: "notify",
            type: "error",
            title: this.$t("ALERTS.ERROR"),
            text: "Паролата не можа да бъде обновена, защото трябва да е уникална."
          });
        })
        .finally(() => {
          this.updatingDriverPasswordIds = this.updatingDriverPasswordIds.filter(
            id => id !== staff.id
          );
        });
    },
    updateStaffType(staff, staffTypeId) {
      const currentStaffTypeId = staff.staffTypeId;

      if (currentStaffTypeId == staffTypeId) {
        return;
      }

      staff.staffTypeId = staffTypeId;
      this.updatingStaffTypeIds.push(staff.id);

      ApiService.put(`staff/${staff.id}/staff-type`, { staffTypeId })
        .then(({ data }) => {
          staff.staffTypeId = data.data.staffTypeId;
          staff.driverPasswordLength = data.data.driverPasswordLength;
          this.$set(this.driverPasswords, staff.id, "");
          this.$notify({
            group: "notify",
            type: "success",
            title: this.$t("ALERTS.SUCCESS"),
            text: "Успешно сменен"
          });
        })
        .catch(() => {
          staff.staffTypeId = currentStaffTypeId;
          this.$notify({
            group: "notify",
            type: "error",
            title: this.$t("ALERTS.ERROR"),
            text: "Типът служител не можа да бъде обновен."
          });
        })
        .finally(() => {
          this.updatingStaffTypeIds = this.updatingStaffTypeIds.filter(
            id => id !== staff.id
          );
        });
    }
  }
};
</script>

<style scoped>
.staff-list ::v-deep .card-header {
  align-items: flex-start;
}

.staff-list ::v-deep .card-toolbar {
  margin-left: auto;
}

.staff-active-control {
  min-height: 32px;
}

.staff-list__status {
  color: #3f4254;
  font-size: 0.85rem;
  font-weight: 600;
  line-height: 1;
}

.staff-list__status-dot {
  background: #3699ff;
  height: 8px;
  min-height: 8px;
  min-width: 8px;
  width: 8px;
}

.staff-list__status-dot--inactive {
  background: #f64e60;
}

.staff-list__mobile-control {
  min-width: 0;
}

@media (max-width: 767.98px) {
  .staff-list ::v-deep .card-header {
    flex-wrap: wrap;
  }

  .staff-list ::v-deep .card-header .card-title,
  .staff-list ::v-deep .card-header .card-toolbar {
    flex: 1 1 100%;
  }

  .staff-list ::v-deep .card-header .card-title {
    margin-bottom: 0.75rem;
  }

  .staff-list ::v-deep .card-header .card-toolbar {
    margin-left: 0;
  }
}
</style>
